import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { flashcards } from '../data/mock';
import { useLanguage } from '../contexts/LanguageContext';
import { 
  ArrowLeft, 
  RotateCcw, 
  Check, 
  X, 
  Brain, 
  Zap, 
  Target,
  TrendingUp,
  Clock,
  Star
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const Training = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { selectedWords, progress } = useLanguage();
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [showTranslation, setShowTranslation] = useState(false);
  const [sessionCards, setSessionCards] = useState([]);
  const [sessionStats, setSessionStats] = useState({
    correct: 0,
    incorrect: 0,
    total: 0
  });
  const [trainingMode, setTrainingMode] = useState('flashcards'); // flashcards, quiz, review
  const [isSessionComplete, setIsSessionComplete] = useState(false);
  const [sessionStartTime, setSessionStartTime] = useState(null);

  useEffect(() => {
    // Initialize training session
    const cardsToTrain = selectedWords.length > 0 ? selectedWords : flashcards;
    setSessionCards(shuffleArray([...cardsToTrain]));
    setSessionStartTime(Date.now());
  }, [selectedWords]);

  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const currentCard = sessionCards[currentCardIndex];
  const progressPercentage = sessionCards.length > 0 ? ((currentCardIndex + 1) / sessionCards.length) * 100 : 0;

  const handleCardFlip = () => {
    setShowTranslation(!showTranslation);
  };

  const handleAnswer = (isCorrect) => {
    const newStats = {
      ...sessionStats,
      total: sessionStats.total + 1,
      correct: isCorrect ? sessionStats.correct + 1 : sessionStats.correct,
      incorrect: isCorrect ? sessionStats.incorrect : sessionStats.incorrect + 1
    };
    setSessionStats(newStats);

    toast({
      title: isCorrect ? "Richtig!" : "Falsch!",
      description: isCorrect ? "Gut gemacht!" : "Nicht aufgeben, weiter üben!",
      duration: 1000,
    });

    // Move to next card
    if (currentCardIndex < sessionCards.length - 1) {
      setCurrentCardIndex(currentCardIndex + 1);
      setShowTranslation(false);
    } else {
      // Session complete
      setIsSessionComplete(true);
    }
  };

  const handleRestart = () => {
    setCurrentCardIndex(0);
    setShowTranslation(false);
    setSessionStats({ correct: 0, incorrect: 0, total: 0 });
    setIsSessionComplete(false);
    setSessionCards(shuffleArray([...sessionCards]));
    setSessionStartTime(Date.now());
  };

  const getSessionDuration = () => {
    if (!sessionStartTime) return 0;
    return Math.floor((Date.now() - sessionStartTime) / 1000 / 60); // minutes
  };

  const getAccuracyRate = () => {
    if (sessionStats.total === 0) return 0;
    return Math.round((sessionStats.correct / sessionStats.total) * 100);
  };

  if (sessionCards.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50">
        <header className="bg-white border-b border-slate-200">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <Button
                variant="ghost"
                onClick={() => navigate('/stories')}
                className="p-2"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <h1 className="text-xl font-bold">Training</h1>
              <div></div>
            </div>
          </div>
        </header>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <Brain className="h-16 w-16 text-slate-400 mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-slate-900 mb-4">
              Keine Wörter zum Trainieren
            </h2>
            <p className="text-slate-600 mb-8">
              Lies eine Geschichte und wähle Wörter aus, die du lernen möchtest.
            </p>
            <Button 
              className="bg-[#4ECDC4] hover:bg-[#45b7aa] text-white"
              onClick={() => navigate('/stories')}
            >
              Geschichten entdecken
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (isSessionComplete) {
    return (
      <div className="min-h-screen bg-slate-50">
        <header className="bg-white border-b border-slate-200">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <Button
                variant="ghost"
                onClick={() => navigate('/stories')}
                className="p-2"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <h1 className="text-xl font-bold">Training abgeschlossen</h1>
              <div></div>
            </div>
          </div>
        </header>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center space-y-8">
            <div className="w-24 h-24 bg-[#4ECDC4] rounded-full flex items-center justify-center mx-auto">
              <Star className="h-12 w-12 text-white" />
            </div>
            
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-4">
                Großartig gemacht!
              </h2>
              <p className="text-xl text-slate-600">
                Du hast deine Trainingseinheit erfolgreich abgeschlossen.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 max-w-2xl mx-auto">
              <Card className="text-center">
                <CardContent className="p-6">
                  <Target className="h-8 w-8 text-[#4ECDC4] mx-auto mb-2" />
                  <div className="text-2xl font-bold text-slate-900">{sessionStats.total}</div>
                  <div className="text-sm text-slate-600">Wörter geübt</div>
                </CardContent>
              </Card>

              <Card className="text-center">
                <CardContent className="p-6">
                  <Check className="h-8 w-8 text-green-500 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-slate-900">{sessionStats.correct}</div>
                  <div className="text-sm text-slate-600">Richtig</div>
                </CardContent>
              </Card>

              <Card className="text-center">
                <CardContent className="p-6">
                  <TrendingUp className="h-8 w-8 text-[#4ECDC4] mx-auto mb-2" />
                  <div className="text-2xl font-bold text-slate-900">{getAccuracyRate()}%</div>
                  <div className="text-sm text-slate-600">Genauigkeit</div>
                </CardContent>
              </Card>

              <Card className="text-center">
                <CardContent className="p-6">
                  <Clock className="h-8 w-8 text-[#4ECDC4] mx-auto mb-2" />
                  <div className="text-2xl font-bold text-slate-900">{getSessionDuration()}</div>
                  <div className="text-sm text-slate-600">Minuten</div>
                </CardContent>
              </Card>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                className="bg-[#4ECDC4] hover:bg-[#45b7aa] text-white"
                onClick={handleRestart}
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Nochmal trainieren
              </Button>
              <Button 
                variant="outline"
                onClick={() => navigate('/stories')}
              >
                Neue Geschichte lesen
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Button
              variant="ghost"
              onClick={() => navigate('/stories')}
              className="p-2"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="text-center">
              <h1 className="text-xl font-bold">Vokabeltraining</h1>
              <div className="text-sm text-slate-600">
                {currentCardIndex + 1} von {sessionCards.length}
              </div>
            </div>
            <Button
              variant="ghost"
              onClick={handleRestart}
              className="p-2"
            >
              <RotateCcw className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Progress */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-slate-600">Fortschritt</span>
            <span className="text-sm text-slate-600">{Math.round(progressPercentage)}%</span>
          </div>
          <Progress value={progressPercentage} className="h-3" />
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <Card className="text-center">
            <CardContent className="p-4">
              <div className="text-lg font-bold text-green-600">{sessionStats.correct}</div>
              <div className="text-sm text-slate-600">Richtig</div>
            </CardContent>
          </Card>
          <Card className="text-center">
            <CardContent className="p-4">
              <div className="text-lg font-bold text-red-600">{sessionStats.incorrect}</div>
              <div className="text-sm text-slate-600">Falsch</div>
            </CardContent>
          </Card>
          <Card className="text-center">
            <CardContent className="p-4">
              <div className="text-lg font-bold text-[#4ECDC4]">{sessionCards.length - currentCardIndex - 1}</div>
              <div className="text-sm text-slate-600">Verbleibend</div>
            </CardContent>
          </Card>
        </div>

        {/* Flashcard */}
        <div className="flex justify-center mb-8">
          <Card 
            className="w-full max-w-lg h-80 cursor-pointer transform transition-transform hover:scale-105 shadow-lg border-0"
            onClick={handleCardFlip}
          >
            <CardContent className="h-full flex flex-col justify-center items-center p-8 text-center">
              {!showTranslation ? (
                <div className="space-y-6">
                  <div className="w-16 h-16 bg-[#4ECDC4] rounded-full flex items-center justify-center mx-auto">
                    <Brain className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold text-slate-900 mb-4">
                      {currentCard.word}
                    </h2>
                    <p className="text-slate-600">
                      Klicke um die Übersetzung zu sehen
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto">
                    <Zap className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold text-slate-900 mb-4">
                      {currentCard.translation}
                    </h2>
                    {currentCard.sentence && (
                      <div className="bg-slate-50 p-4 rounded-lg">
                        <p className="text-slate-700 italic">
                          "{currentCard.sentence}"
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Answer Buttons */}
        {showTranslation && (
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              variant="outline"
              className="h-14 px-8 border-red-200 text-red-700 hover:bg-red-50 hover:border-red-300"
              onClick={() => handleAnswer(false)}
            >
              <X className="h-5 w-5 mr-2" />
              Schwierig
            </Button>
            <Button
              className="h-14 px-8 bg-green-600 hover:bg-green-700 text-white"
              onClick={() => handleAnswer(true)}
            >
              <Check className="h-5 w-5 mr-2" />
              Kannte ich
            </Button>
          </div>
        )}

        {/* Help Text */}
        <div className="text-center mt-8">
          <p className="text-slate-600">
            {!showTranslation 
              ? "Versuche dich an die Bedeutung zu erinnern, dann klicke auf die Karte."
              : "Wie gut kanntest du dieses Wort?"
            }
          </p>
        </div>
      </div>
    </div>
  );
};

export default Training;